/**
 * Global search command palette modal.
 * Opened via Cmd+K / Ctrl+K or the search button in the header.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';
import { GlobalSearchResults, tagResultUrls } from './GlobalSearchResults';
import { GlobalSearchFilters } from './GlobalSearchFilters';
import { GlobalSearchFacets } from './GlobalSearchFacets';
import { useModule } from '../../contexts/ModuleContext';

interface GlobalSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearchModal({ open, onOpenChange }: GlobalSearchModalProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { currentModule } = useModule();

  const {
    query,
    setQuery,
    filters,
    setFilters,
    moduleResults,
    pageResults,
    suggestedPages,
    entityQuery,
    resultCount,
    isLoading,
  } = useGlobalSearch({ defaultModule: currentModule.id === 'home' ? 'all' : currentModule.id });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setFiltersOpen(false);
      // Reset module filter to current module (or 'all' on home page)
      setFilters((prev) => ({
        ...prev,
        modules: [currentModule.id === 'home' ? 'all' : currentModule.id],
      }));
    }
  }, [open, currentModule.id, setQuery, setFilters]);

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      // Small delay to ensure the dialog is rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Build flat list of all results for keyboard navigation.
  // Order must match GlobalSearchResults: modules → pages → tags → entities → suggestions.
  const allResults = useMemo(() => {
    const hasQuery = query.length >= 2 || moduleResults.length > 0 || pageResults.length > 0;
    if (hasQuery) {
      return [
        ...moduleResults.map((m) => m.url),
        ...pageResults.map((p) => p.url),
        ...tagResultUrls(entityQuery.data?.tags || []),
        ...(entityQuery.data?.entities.map((e) => e.url) || []),
        ...(entityQuery.data?.suggestions?.map((e) => e.url) || []),
      ];
    }
    return suggestedPages.map((p) => p.url);
  }, [query, moduleResults, pageResults, suggestedPages, entityQuery.data?.tags, entityQuery.data?.entities, entityQuery.data?.suggestions]);

  const totalResults = allResults.length;

  // Toggle an entity type in the facet filter.
  const handleToggleEntityType = useCallback(
    (entityType: string) => {
      setFilters((prev) => ({
        ...prev,
        entityTypes: prev.entityTypes.includes(entityType)
          ? prev.entityTypes.filter((et) => et !== entityType)
          : [...prev.entityTypes, entityType],
      }));
    },
    [setFilters],
  );

  // Navigate to selected result and close modal
  const handleSelect = useCallback(
    (url: string) => {
      onOpenChange(false);
      navigate(url);
    },
    [navigate, onOpenChange],
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % Math.max(totalResults, 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev <= 0 ? Math.max(totalResults - 1, 0) : prev - 1,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (allResults[selectedIndex]) {
            handleSelect(allResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onOpenChange(false);
          break;
      }
    },
    [totalResults, allResults, selectedIndex, handleSelect, onOpenChange],
  );

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, entityQuery.data]);

  // Detect platform for shortcut label
  const isMac = navigator.platform.toUpperCase().includes('MAC');
  const shortcutLabel = isMac ? '\u2318K' : 'Ctrl+K';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 overflow-hidden"
        onKeyDown={handleKeyDown}
        // Prevent closing on interact outside when typing
        onInteractOutside={(e) => e.preventDefault()}
      >
        <VisuallyHidden>
          <DialogTitle>{t('globalSearch.placeholder')}</DialogTitle>
          <DialogDescription>{t('globalSearch.typeToSearch', 'Type to search...')}</DialogDescription>
        </VisuallyHidden>
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('globalSearch.placeholder')}
            data-testid="global-search-input"
            className="border-0 shadow-none focus-visible:ring-0 text-base h-auto p-0"
          />
          <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            {shortcutLabel}
          </kbd>
        </div>

        {/* Filters */}
        <GlobalSearchFilters
          filters={filters}
          onFiltersChange={setFilters}
          isOpen={filtersOpen}
          onToggle={() => setFiltersOpen(!filtersOpen)}
        />

        {/* Entity-type facet chips */}
        <GlobalSearchFacets
          facets={query.length >= 2 ? entityQuery.data?.facets || [] : []}
          selected={filters.entityTypes}
          onToggle={handleToggleEntityType}
        />

        {/* Results */}
        <GlobalSearchResults
          query={query}
          modules={moduleResults}
          pages={pageResults}
          suggestedPages={suggestedPages}
          entities={entityQuery.data?.entities || []}
          tags={entityQuery.data?.tags || []}
          suggestions={entityQuery.data?.suggestions || []}
          matchTerms={entityQuery.data?.match_terms || []}
          isLoading={isLoading}
          selectedIndex={selectedIndex}
          onSelect={handleSelect}
        />

        {/* Footer */}
        <div className="border-t">
          {totalResults > 0 && (
            <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
              <span>
                {query.length > 0 ? t('globalSearch.results', { count: resultCount }) : null}
              </span>
              <span>
{/* eslint-disable-next-line i18next/no-literal-string */}
                <kbd className="px-1 py-0.5 rounded border bg-muted font-mono text-[10px]">Enter</kbd>
                {' '}{t('globalSearch.toOpen')}
                {' '}<kbd className="px-1 py-0.5 rounded border bg-muted font-mono text-[10px]">&uarr;&darr;</kbd>
                {' '}{t('globalSearch.toNavigate')}
              </span>
            </div>
          )}
          {/* Power-user query syntax hint (Tier 2) */}
          <div className="px-4 py-1.5 text-[11px] text-muted-foreground/70 truncate">
            {t('globalSearch.syntaxHint')}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
